import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { FlatList } from 'react-native';
import { useTranslation } from '@betty/i18n';
import { selectAuthenticationStatus, useSelector } from '@betty/toolkit/state';
import { Text } from '@betty/moonlight';
import { formatSearchText } from './Results.utils';
import { ResultsProps } from './Results.types';
import GamesGrid from '../../GamesGrid/GamesGrid';
import NoResults from '../NoResults/NoResults';
import GamesService from '../../../services/GamesService/GamesService';
import { Game } from '../../../state/shared/games.types';

function Results({ searchText }: ResultsProps) {
  const { t } = useTranslation('games');
  const listRef = useRef<FlatList>(null);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<Game['id'][]>([]);
  const authStatus = useSelector(selectAuthenticationStatus);
  const count = results.length;

  const search = useCallback(
    async (searchText: string) => {
      try {
        setLoading(true);

        const params = {
          SearchTerm: formatSearchText(searchText),
        };

        const { gameIds } =
          authStatus === 'authenticated'
            ? await GamesService.getSearchResults({ params })
            : await GamesService.getPublicSearchResults({ params });

        setResults(gameIds);
      } catch (_) {
        // TODO: log error
      } finally {
        setLoading(false);
      }
    },
    [authStatus],
  );

  const renderHeader = useCallback(
    () =>
      !loading && !count ? (
        <NoResults />
      ) : (
        <Text
          variant="body-m-semi"
          color="darkSurfacePrimary"
          paddingBottom="space-300"
        >
          {t('found', { count })}
        </Text>
      ),
    [count, loading, t],
  );

  useEffect(() => {
    listRef.current?.scrollToOffset({
      offset: 0,
      animated: true,
    });
  }, [searchText]);

  useEffect(() => {
    search(searchText);
  }, [search, searchText]);

  return (
    <GamesGrid
      ref={listRef}
      gameIds={results}
      loading={loading}
      imageType="square"
      numOfColumns={4}
      contentContainerStyle={{
        paddingBottom: 'safe-area-inset-bottom',
        paddingHorizontal: 'space-600',
      }}
      ListHeaderComponent={renderHeader}
    />
  );
}

export default memo(Results);
